import React, { useEffect, useState } from 'react';
import {
  ALL_HOMEBREW_KEYS,
  FEATURES,
  HOMEBREW_GROUPS,
} from './forge-v2/constants';

const DEFAULT_FEATURES = Object.fromEntries(FEATURES.map((feature) => [feature.key, true]));
const DEFAULT_HOMEBREW = Object.fromEntries(ALL_HOMEBREW_KEYS.map((key) => [key, true]));
const MAX_NAME = 120;
const MAX_TAGLINE = 160;

const normalizeFeatureState = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  return Object.fromEntries(FEATURES.map((feature) => [feature.key, source[feature.key] !== false]));
};

const normalizeHomebrewState = (value) => {
  if (!value || typeof value !== 'object' || Object.keys(value).length === 0) {
    return { ...DEFAULT_HOMEBREW };
  }
  return Object.fromEntries(ALL_HOMEBREW_KEYS.map((key) => [key, value[key] === true]));
};

const fieldStyle = (theme) => ({
  width: '100%',
  background: 'rgba(0,0,0,0.32)',
  border: `1px solid ${theme.colors.borderLight}`,
  color: theme.colors.textPrimary,
  borderRadius: theme.effects?.borderRadiusSm || '4px',
  padding: '10px 12px',
  fontSize: '0.92rem',
  fontFamily: theme.typography?.fontFamily || 'serif',
  boxSizing: 'border-box',
});

const labelStyle = (theme) => ({
  display: 'block',
  marginBottom: 6,
  fontFamily: theme.typography?.fontFamily,
  fontSize: '0.78rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.colors.textMuted,
});

const helperStyle = (theme) => ({
  marginTop: 4,
  fontFamily: theme.typography?.fontFamily,
  fontSize: '0.74rem',
  color: theme.colors.textMuted,
});

export const SandboxCampaignSettingsModal = ({
  theme,
  settings = {},
  onClose,
  onSave,
}) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const [campaignName, setCampaignName] = useState('');
  const [campaignTagline, setCampaignTagline] = useState('');
  const [enabledFeatures, setEnabledFeatures] = useState(() => ({ ...DEFAULT_FEATURES }));
  const [homebrew, setHomebrew] = useState(() => ({ ...DEFAULT_HOMEBREW }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setCampaignName(settings.campaignName || 'Campaign Sandbox');
    setCampaignTagline(settings.campaignTagline || '');
    setEnabledFeatures(normalizeFeatureState(settings.enabledFeatures));
    setHomebrew(normalizeHomebrewState(settings.homebrew));
    setSaving(false);
    setError(null);
  }, [settings]);

  const toggleFeature = (key) => {
    setEnabledFeatures((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleHomebrew = (key) => {
    setHomebrew((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    if (!onSave) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        campaignName,
        campaignTagline,
        enabledFeatures,
        homebrew,
      });
      onClose && onClose();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Sandbox campaign settings"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        zIndex: 210,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: 560,
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
          background: c.bgCardSolid,
          border: `1px solid ${c.borderPrimary}`,
          borderRadius: theme.effects?.borderRadiusLg || '12px',
          padding: 24,
          boxShadow: '0 18px 48px rgba(0,0,0,0.6)',
          color: c.textPrimary,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 18 }}>
          <h2 style={{
            margin: 0,
            fontFamily: t.headingFamily,
            fontSize: '1.3rem',
            color: c.accent,
          }}>Sandbox Settings</h2>
          <span style={{ color: c.textMuted, fontSize: '0.86rem' }}>Local only</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle(theme)} htmlFor="sandbox-campaign-name">Sandbox Name</label>
            <input
              id="sandbox-campaign-name"
              type="text"
              value={campaignName}
              maxLength={MAX_NAME}
              onChange={(e) => setCampaignName(e.target.value)}
              style={fieldStyle(theme)}
            />
            <div style={helperStyle(theme)}>{campaignName.length} / {MAX_NAME} characters</div>
          </div>

          <div>
            <label style={labelStyle(theme)} htmlFor="sandbox-campaign-tagline">Tagline</label>
            <input
              id="sandbox-campaign-tagline"
              type="text"
              value={campaignTagline}
              maxLength={MAX_TAGLINE}
              placeholder="A short note for this local sandbox"
              onChange={(e) => setCampaignTagline(e.target.value)}
              style={fieldStyle(theme)}
            />
            <div style={helperStyle(theme)}>{campaignTagline.length} / {MAX_TAGLINE} characters</div>
          </div>

          <div>
            <div style={labelStyle(theme)}>Sandbox Features</div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 8,
            }}>
              {FEATURES.map((feature) => (
                <label
                  key={feature.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    border: `1px solid ${theme.colors.borderLight}`,
                    borderRadius: 4,
                    color: theme.colors.textSecondary,
                    fontFamily: t.fontFamily,
                    fontSize: '0.82rem',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={enabledFeatures[feature.key] !== false}
                    onChange={() => toggleFeature(feature.key)}
                  />
                  <span>{feature.label}</span>
                </label>
              ))}
            </div>
            <div style={helperStyle(theme)}>Audit Log remains available locally and is not a selectable feature.</div>
          </div>

          <div>
            <div style={labelStyle(theme)}>Homebrew</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {HOMEBREW_GROUPS.map((group) => (
                <div key={group.key}>
                  <div style={{ ...helperStyle(theme), marginTop: 0, marginBottom: 6 }}>{group.label}</div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: 8,
                  }}>
                    {group.items.map((item) => (
                      <label
                        key={item.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 10px',
                          border: `1px solid ${theme.colors.borderLight}`,
                          borderRadius: 4,
                          color: theme.colors.textSecondary,
                          fontFamily: t.fontFamily,
                          fontSize: '0.82rem',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={homebrew[item.key] === true}
                          onChange={() => toggleHomebrew(item.key)}
                        />
                        <span>{item.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div style={{
            marginTop: 16,
            padding: '8px 12px',
            background: 'rgba(212,106,90,0.15)',
            border: `1px solid ${c.danger || '#d46a5a'}`,
            color: c.danger || '#d46a5a',
            borderRadius: 4,
            fontSize: '0.86rem',
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '9px 16px',
              background: 'transparent',
              color: c.textSecondary,
              border: `1px solid ${c.borderLight}`,
              borderRadius: 4,
              cursor: saving ? 'wait' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '9px 18px',
              background: c.accent,
              color: c.textOnAccent,
              border: `1px solid ${c.accent}`,
              borderRadius: 4,
              cursor: saving ? 'wait' : 'pointer',
              fontWeight: 700,
            }}
          >
            {saving ? 'Saving...' : 'Save local settings'}
          </button>
        </div>
      </form>
    </div>
  );
};
